<template>
  <v-card
    elevation="0"
    class="playposit-bulb-settings"
    :ripple="false"
    @click="localSelectedBulbSettings = localBulb.id"
  >
    <v-form
      class="width-100"
      v-model="isFormValid"
    >
      <v-layout
        justify-center
        align-center
        column
        class="width-100"
        pa-3
      >
        <v-layout
          v-if="service !== 'batchApplyToAll'"
          class="width-100"
          justify-center
          align-center
          :column="wrapperSize < 600"
          px-2
        >
          <v-flex
            :xs5="wrapperSize >= 600"
            class="mb-3"
            :class="{
              'width-100': wrapperSize < 600,
            }"
          >
            <v-layout
              justify-center
              align-center
            >
              <PlaypositImageUpload
                :image="localBulb.thumbnail"
                @processing="isProcessing = !!$event"
                @success="$set(localBulb, 'thumbnail', $event)"
                @error="$emit('error', $event)"
              />
            </v-layout>
          </v-flex>
          <v-flex
            :xs7="wrapperSize >= 600"
            :class="{
              'width-100': wrapperSize < 600,
              'pl-3': wrapperSize >= 600
            }"
          >
            <v-text-field
              @paste="handleTextPasted"
              :key="titleInputKey"
              :color="activeColor"
              label="Title"
              :aria-label="`Text input field for this ${termForBulb}'s title.`"
              v-model="sanitizedVideoName"
              :maxlength="250"
              :rules="rules.name"
              required
              :loading="isLoading && selectedField === 'name'"
            ></v-text-field>
            <v-text-field
              :color="activeColor"
              label="Learning objective"
              :aria-label="`Text input field for this ${termForBulb}'s learning objective.`"
              v-model="localBulb.learningObjective"
              :maxlength="250"
              :loading="isLoading && selectedField === 'learningObjective'"
            ></v-text-field>
          </v-flex>
        </v-layout>
        <!-- Older microservices, since they use an older version of vuetify... -->
        <v-expansion-panel
          class="elevation-0"
          v-if="!['playlist', 'designer'].includes(service)"
          v-show="!modes.includes('lmb')"
          v-model="openedExpansionPanel"
        >
          <v-expansion-panel-content
            :disabled="modes.includes('lmb')"
          >
            <template v-slot:header>
              <v-layout
                align-center
                justify-start
              >
                <v-icon left>
                  bookmark_border
                </v-icon>
                Tags
                <v-icon
                  v-if="hasTagErrors"
                  right
                  color="error"
                >
                  error
                </v-icon>
                <ChickFilATagButton
                  :school="school"
                  service="bulb-settings"/>
              </v-layout>
            </template>
            <v-card class="px-3">
              <v-combobox
                :color="activeColor"
                v-model="localBulb.keywords"
                :items="tags.keywords"
                :rules="rules.tags"
                :search-input.sync="tagSearchString"
                label="Keywords/Tags"
                chips
                flat
                clearable
                multiple
                ref="keywords"
                @input="clearTagSearchString"
                :loading="isSearching || (isLoading && selectedField === 'keywords')"
                :disabled="modes.includes('lmb')"
                :success-messages="message.keywords"
                @blur="saveOnChange('keywords')"
              >
                <template slot="selection" slot-scope="{ item, index }">
                  <v-chip
                    :style="service === 'batch' ? tagStyles['user-generated'] : getTagStyles('user-generated', item)"
                    close
                    @input="handleChipRemoval({
                      index,
                      type: 'keywords',
                    })"
                  >
                    <strong>{{ item }}</strong>
                  </v-chip>
                </template>
              </v-combobox>
              <v-autocomplete
                v-if="!isProquestBulb"
                :color="activeColor"
                class="select-overflow"
                v-model="localBulb.age"
                :items="tags.age"
                label="Target Audience"
                chips
                flat
                clearable
                multiple
                ref="age"
                :disabled="modes.includes('lmb')"
                :loading="isLoading && selectedField === 'age'"
                :success-messages="message.age"
                @change="toggleMenu('age')"
                @blur="saveOnChange('age')"
              >
                <template slot="selection" slot-scope="{ item, index }">
                  <v-chip
                    :style="service === 'batch' ? tagStyles.age : getTagStyles('age', item)"
                    close
                    @input="handleChipRemoval({
                      index,
                      type: 'age',
                    })"
                  >
                    <strong>
                      {{ item }}
                    </strong>&nbsp;
                  </v-chip>
                </template>
              </v-autocomplete>
              <v-autocomplete
                :color="activeColor"
                class="select-overflow"
                v-model="tagType"
                :items="isProquestBulb ? tags.proquest : tags.subject"
                label="Subject"
                chips
                flat
                clearable
                multiple
                ref="subject"
                :disabled="modes.includes('lmb')"
                :loading="isLoading && selectedField === 'subject'"
                :success-messages="message.subject"
                @change="toggleMenu('subject')"
                @blur="saveOnChange(isProquestBulb ? 'proquest' : 'subject')"
              >
                <template slot="selection" slot-scope="{ item, index }">
                  <v-chip
                    :style="service === 'batch' ? tagStyles.subject : getTagStyles('subject', item)"
                    close
                    @input="handleChipRemoval({
                      index,
                      type: isProquestBulb ? 'proquest' : 'subject',
                    })"
                  >
                    <strong>
                      {{ item }}
                    </strong>&nbsp;
                  </v-chip>
                </template>
              </v-autocomplete>
            </v-card>
          </v-expansion-panel-content>
          <v-expansion-panel-content
            :disabled="modes.includes('lmb')"
          >
            <template v-slot:header>
              <v-layout
                align-center
                justify-start
              >
                <v-icon left>
                  play_circle_outline
                </v-icon>
                Playback options
              </v-layout>
            </template>
            <v-card class="px-3">
              <v-checkbox
                label="Allow learners to rewind after an interaction appears"
                :color="activeColor"
                v-model="localBulb.config.rewind_yes"
                class="mb-3"
                :true-value="1"
                :false-value="0"
                :disabled="modes.includes('lmb')"
                @change="localBulb.apply_all.rewind_yes = 0"
                hide-details
              ></v-checkbox>
              <v-checkbox
                v-show="localBulb.config.rewind_yes !== originalBulb.config.rewind_yes"
                :color="activeColor"
                label="Apply this option to all existing interactions"
                v-model="localBulb.apply_all.rewind_yes"
                class="ml-4 small-label mb-3"
                :true-value="1"
                :false-value="0"
                :disabled="modes.includes('lmb')"
                hide-details
              ></v-checkbox>
              <v-checkbox
                :color="activeColor"
                label="Allow learners to skip interactions"
                v-model="localBulb.config.skip_yes"
                class="mb-3"
                :true-value="1"
                :false-value="0"
                :disabled="modes.includes('lmb')"
                @change="localBulb.apply_all.skip_yes = 0"
                hide-details
              ></v-checkbox>
              <v-checkbox
                v-show="localBulb.config.skip_yes !== originalBulb.config.skip_yes"
                :color="activeColor"
                label="Apply this option to all existing interactions"
                v-model="localBulb.apply_all.skip_yes"
                class="ml-4 small-label mb-3"
                :true-value="1"
                :false-value="0"
                :disabled="modes.includes('lmb')"
                hide-details
              ></v-checkbox>
              <v-checkbox
                :color="activeColor"
                label="Allow viewers to fast forward"
                v-model="localBulb.config.fast_yes"
                class="mb-3"
                :true-value="1"
                :false-value="0"
                :disabled="modes.includes('lmb')"
                hide-details
              ></v-checkbox>
              <v-checkbox
                :color="activeColor"
                :label="`Allow learners to retake the ${termForBulb} upon completion`"
                v-model="localBulb.config.retry_yes"
                class="mb-3"
                :true-value="1"
                :false-value="0"
                :disabled="modes.includes('lmb')"
                @change="handleBulbRetakeChange"
                hide-details
              ></v-checkbox>
              <v-text-field
                @blur="handleBulbRetakeLimitBlur(localBulb.config.attempts)"
                ref="bulbRetakeLimitInput"
                v-show="localBulb.config.retry_yes"
                v-model.number="localBulb.config.attempts"
                :color="activeColor"
                label="Number of retakes"
                type="number"
                class="ml-4 small-label mb-3"
                :min="0"
                :max="1000"
                :disabled="modes.includes('lmb')"
                :hint="`A value of 0 means that the ${termForBulb} can be retaken indefinitely.`"
                :persistent-hint="localBulb.config.attempts === 0"
              ></v-text-field>
              <v-checkbox
                @change="handlePrintableReportChange($event)"
                v-if="isSchoolTeacher"
                :color="activeColor"
                label="Allow learners to view their printable report"
                v-model="localBulb.config.export_yes"
                class="mb-3"
                :true-value="1"
                :false-value="0"
                :disabled="modes.includes('lmb')"
                hide-details
              ></v-checkbox>
              <v-checkbox
                @change="handleSubPrintableReportChange('enableViewReport', $event)"
                v-if="(
                  isSchoolTeacher
                  && localBulb.config.export_yes
                )"
                ref="enableViewReportCheckbox"
                :color="activeColor"
                label="Allow learners to view their report in a new tab"
                v-model="localBulb.config.enableViewReport"
                class="ml-4 small-label mb-3"
                :true-value="1"
                :false-value="0"
                :disabled="modes.includes('lmb')"
                hide-details
              ></v-checkbox>
              <v-checkbox
                @change="handleSubPrintableReportChange('enablePdfReport', $event)"
                v-if="(
                  isSchoolTeacher
                  && localBulb.config.export_yes
                  && permissionKeys.includes('showDownloadPdfBulbResultsButton')
                )"
                ref="enablePdfReportCheckbox"
                :color="activeColor"
                label="Allow learners to download their report in PDF format"
                v-model="localBulb.config.enablePdfReport"
                class="ml-4 small-label mb-3"
                :true-value="1"
                :false-value="0"
                :disabled="modes.includes('lmb')"
                hide-details
              ></v-checkbox>
              <v-checkbox
                @change="handleSubPrintableReportChange('enableHtmlReport', $event)"
                v-if="(
                  isSchoolTeacher
                  && localBulb.config.export_yes
                  && permissionKeys.includes('showDownloadHtmlBulbResultsButton')
                )"
                ref="enableHtmlReportCheckbox"
                :color="activeColor"
                label="Allow learners to download their report in HTML format"
                v-model="localBulb.config.enableHtmlReport"
                class="ml-4 small-label mb-3"
                :true-value="1"
                :false-value="0"
                :disabled="modes.includes('lmb')"
                hide-details
              ></v-checkbox>
              <v-checkbox
                @change="handleSubPrintableReportChange('enableDocxReport', $event)"
                v-if="(
                  isSchoolTeacher
                  && localBulb.config.export_yes
                  && permissionKeys.includes('showDownloadDocxBulbResultsButton')
                )"
                ref="enableDocxReportCheckbox"
                :color="activeColor"
                label="Allow learners to download their report in DOCX format"
                v-model="localBulb.config.enableDocxReport"
                class="ml-4 small-label mb-3"
                :true-value="1"
                :false-value="0"
                :disabled="modes.includes('lmb')"
                hide-details
              ></v-checkbox>
              <v-checkbox
                v-if="isPremiumOrSchoolTeacher"
                :color="activeColor"
                label="Allow learners to increase playback speed"
                v-model="localBulb.config.fast_playback"
                class="mb-3"
                :true-value="1"
                :false-value="0"
                :disabled="modes.includes('lmb')"
                hide-details
              ></v-checkbox>
              <v-checkbox
                v-if="isSchoolTeacher"
                :color="activeColor"
                label="Disable captions for this video"
                v-model="localBulb.config.captions_disabled"
                @change="handleDisableCaptionsChange"
                class="mb-3"
                :true-value="1"
                :false-value="0"
                :disabled="modes.includes('lmb')"
                hide-details
              ></v-checkbox>
              <v-checkbox
                v-if="localBulb.config.captions_disabled"
                :color="activeColor"
                label="Make transcript available"
                v-model="localBulb.config.enable_transcript"
                class="mb-3 ml-4"
                :true-value="1"
                :false-value="0"
                :disabled="modes.includes('lmb')"
                hide-details
              ></v-checkbox>
              <v-checkbox
                :color="activeColor"
                :label="`Hide learner score upon ${termForBulb} completion`"
                v-model="localBulb.config.hide_learner_score"
                class="mb-3"
                :true-value="1"
                :false-value="0"
                :disabled="modes.includes('lmb')"
                hide-details
              ></v-checkbox>
              <Tooltip
                v-if="service !== 'batchApplyToAll'"
                :attach="'.score-threshold-tooltip'"
                :disabled="isScoreThresholdToggleEnabled"
                top>
                <div>
                  <span class="score-threshold-tooltip"></span>
                  <v-checkbox
                    :color="activeColor"
                    v-model="isScoreThresholdEnabled"
                    :disabled="!isScoreThresholdToggleEnabled"
                    hide-details
                    class="mb-3">
                    <template v-slot:label>
                      <div>
                        Do not mark {{termForBulb}} complete unless learner scores at least
                        <v-text-field
                          ref="scoreThresholdInput"
                          class="percentage-number-input ma-0 pa-0"
                          :color="activeColor"
                          @click.stop
                          @blur="handleScoreThresholdBlur(scoreThreshold)"
                          v-model.number="scoreThreshold"
                          type="number"
                          :disabled="!isScoreThresholdEnabled || !isScoreThresholdToggleEnabled"
                          :placeholder="scoreThresholdPlaceholder"
                          :min="1"
                          :max="100"
                          hide-details
                          :error-messages="scoreThresholdError"
                        ></v-text-field>
                      </div>
                    </template>
                  </v-checkbox>
                  <div v-if="isScoreThresholdToggleEnabled" class="caption grey--text indented-text mb-2">
                    {{scoreThresholdString}}
                  </div>
                </div>
                <template v-slot:text>
                  <span class="white--text">
                    Need one interaction worth points, retakes enabled, and a percentage threshold to mark {{termForBulb}} complete.
                  </span>
                </template>
              </Tooltip>
            </v-card>
          </v-expansion-panel-content>
          <v-expansion-panel-content
            :disabled="modes.includes('lmb')"
          >
            <template v-slot:header>
              <v-layout
                align-center
                justify-start
              >
                <v-icon left>
                  vpn_key
                </v-icon>
                Privacy
              </v-layout>
            </template>
            <v-card class="px-3">
              <v-radio-group
                v-model="localBulb.privacy"
                :color="activeColor"
                :disabled="modes.includes('lmb')"
                :hide-details="!message.privacy"
                :success-messages="message.privacy"
              >
                <v-radio
                  :color="activeColor"
                  :value="1"
                >
                  <template slot="label">
                    <v-list class="privacyLabelStyle" :two-line="isProquestBulb">
                      <v-list-tile>
                        <v-list-tile-content>
                          <v-list-tile-title>
                            <v-icon v-if="this.isProquestBulb" size=24 class="pr-2">
                              visibility
                            </v-icon>
                            {{ this.privacyLabels.public }}
                          </v-list-tile-title>
                          <v-list-tile-sub-title v-if="this.isProquestBulb">
                            Everyone can share or copy your {{termForBulb}}.
                          </v-list-tile-sub-title>
                        </v-list-tile-content>
                      </v-list-tile>
                    </v-list>
                  </template>
                </v-radio>
                <v-radio
                  v-if="!isProquestBulb"
                  :color="activeColor"
                  :disabled="!isSchoolTeacher"
                  :value="2"
                >
                  <template slot="label">
                    <v-list class="privacyLabelStyle" :two-line="isProquestBulb">
                      <v-list-tile>
                        <v-list-tile-content>
                          <v-list-tile-title>
                            {{ this.privacyLabels.district }}
                          </v-list-tile-title>
                        </v-list-tile-content>
                      </v-list-tile>
                    </v-list>
                  </template>
                </v-radio>
                <v-radio
                  :color="activeColor"
                  :disabled="!isSchoolTeacher"
                  :value="3"
                >
                  <template slot="label">
                    <v-list class="privacyLabelStyle" :two-line="isProquestBulb">
                      <v-list-tile>
                        <v-list-tile-content>
                          <v-list-tile-title>
                            <v-icon v-if="this.isProquestBulb" size=24 class="pr-2">
                              account_balance
                            </v-icon>
                            {{ this.privacyLabels.school }}
                          </v-list-tile-title>
                          <v-list-tile-sub-title v-if="this.isProquestBulb">
                            Anyone within your institution can share or copy your {{termForBulb}}.
                          </v-list-tile-sub-title>
                        </v-list-tile-content>
                      </v-list-tile>
                    </v-list>
                  </template>
                </v-radio>
                <v-radio
                  :color="activeColor"
                  :value="5"
                >
                  <template slot="label">
                    <v-list class="privacyLabelStyle" :two-line="isProquestBulb">
                      <v-list-tile>
                        <v-list-tile-content>
                          <v-list-tile-title>
                            <v-icon v-if="this.isProquestBulb" size=24 class="pr-2">
                              visibility_off
                            </v-icon>
                            {{ this.privacyLabels.private }}
                          </v-list-tile-title>
                          <v-list-tile-sub-title v-if="this.isProquestBulb">
                            Only you can share or copy your {{termForBulb}}.
                          </v-list-tile-sub-title>
                        </v-list-tile-content>
                      </v-list-tile>
                    </v-list>
                  </template>
                </v-radio>
              </v-radio-group>
            </v-card>
          </v-expansion-panel-content>
          <v-expansion-panel-content
            v-if="isBulbCheckoutEnabled"
            :disabled="modes.includes('lmb')">
            <template v-slot:header>
              <v-layout
                align-center
                justify-start
              >
                <v-icon left>
                  point_of_sale
                </v-icon>
                {{termForBulb.capitalize()}} Checkout
              </v-layout>
            </template>
            <v-card class="px-3">
              <v-select
                :items="bulbCheckoutActions"
                v-model="checkoutDetails.action"
                :menu-props="{ contentClass: 'bulb-settings-select-menu' }"
                label="Action"
              >
                <template slot="item" slot-scope="{ item }">
                  <div>{{ item.text }}</div>
                  <div
                    v-if="item.description"
                    class="caption grey--text pl-4"
                  >
                    {{ item.description }}
                  </div>
                </template>
              </v-select>
              <v-text-field
                :value="checkoutDetails.enabled ? 'Enabled' : 'Disabled'"
                :color="activeColor"
                disabled
                label="Checkout Status"
                prepend-icon="description"
              >
              </v-text-field>
              <v-text-field
                @input="$set(checkoutDetails, 'studentEmail', $event)"
                :value="checkoutDetails.studentEmail"
                :color="activeColor"
                :loading="isLoadingStudentEmail"
                :disabled="!areBulbCheckoutFieldsEnabled"
                label="Learner email"
                prepend-icon="email"
              >
              </v-text-field>
              <v-text-field
                @input="$set(checkoutDetails, 'duration', $event)"
                :value="checkoutDetails.duration"
                :color="activeColor"
                :disabled="!areBulbCheckoutFieldsEnabled"
                label="Duration"
                prepend-icon="timer"
                type="number"
              >
              </v-text-field>
              <v-menu
                ref="checkoutDateMenu"
                v-model="checkoutDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                :return-value.sync="checkoutDetails.checkoutDate"
                :disabled="!areBulbCheckoutFieldsEnabled"
                lazy
                transition="scale-transition"
                offset-y
                full-width
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="checkoutDetails.checkoutDate"
                    :disabled="!areBulbCheckoutFieldsEnabled"
                    label="Checkout Date"
                    prepend-icon="event"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="checkoutDetails.checkoutDate"
                  no-title
                  scrollable
                  :min="new Date().toISOString().substr(0, 10)"
                  :disabled="!areBulbCheckoutFieldsEnabled"
                >
                  <v-spacer></v-spacer>
                  <v-btn
                    flat
                    color="primary"
                    @click="checkoutDateMenu = false">
                    Cancel
                  </v-btn>
                  <v-btn
                    flat
                    color="primary"
                    @click="$refs.checkoutDateMenu.save(checkoutDetails.checkoutDate)">
                    OK
                  </v-btn>
                </v-date-picker>
              </v-menu>
              <v-menu
                ref="menu"
                v-model="checkoutTimeMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                :return-value.sync="checkoutDetails.checkoutTime"
                :disabled="!areBulbCheckoutFieldsEnabled"
                lazy
                transition="scale-transition"
                offset-y
                full-width
                max-width="350px"
                min-width="350px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :value="formattedCheckoutTime"
                    :disabled="!areBulbCheckoutFieldsEnabled"
                    label="Checkout Time"
                    prepend-icon="access_time"
                    readonly
                    v-on="on"
                    persistent-hint
                    :hint="timezone ? `Timezone: ${timezone}` : ''"
                  ></v-text-field>
                </template>
                <v-time-picker
                  v-if="checkoutTimeMenu"
                  v-model="checkoutDetails.checkoutTime"
                  full-width
                  use-seconds
                  :min="timePickerMinimumTime"
                  :disabled="!areBulbCheckoutFieldsEnabled"
                  @click:second="$refs.menu.save(checkoutDetails.checkoutTime)"
                ></v-time-picker>
              </v-menu>
            </v-card>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <!--
          Playlist and Designer microservice expansion list. Can't use the above since Playlists and Designer use a
          newer version of vuetify.
        -->
        <v-expansion-panels
          v-if="['playlist', 'designer'].includes(service)"
          accordian
          v-model="openedExpansionPanel"
          v-show="!modes.includes('lmb')"
        >
          <v-expansion-panel>
            <v-expansion-panel-header disable-icon-rotate>
              Tags
              <ChickFilATagButton
                  :school="school"
                  service="playlist-bulb-settings"
              />
              <template v-slot:actions>
                <v-icon color="primary">
                  bookmark_outline
                </v-icon>
              </template>
            </v-expansion-panel-header>
            <v-expansion-panel-content
            :disabled="modes.includes('lmb')"
            >
            <v-combobox
              :color="activeColor"
              v-model="localBulb.keywords"
              :items="tags.keywords"
              :rules="rules.tags"
              :search-input.sync="tagSearchString"
              label="Keywords/Tags"
              chips
              flat
              clearable
              multiple
              ref="keywords"
              @input="clearTagSearchString"
              :loading="isSearching || (isLoading && selectedField === 'keywords')"
              :disabled="modes.includes('lmb')"
              :success-messages="message.keywords"
              @blur="saveOnChange('keywords')"
            >
              <template slot="selection" slot-scope="{ item, index }">
                <v-chip
                  :style="service === 'batch' ? tagStyles['user-generated'] : getTagStyles('user-generated', item)"
                  close
                  @[vChipAttributeName] = "handleChipRemoval({
                    index,
                    type: 'keywords',
                  })"
                >
                  <strong>{{ item }}</strong>
                </v-chip>
              </template>
            </v-combobox>
            <v-autocomplete
              v-if="!isProquestBulb"
              :color="activeColor"
              class="select-overflow"
              v-model="localBulb.age"
              :items="tags.age"
              label="Target Audience"
              chips
              flat
              clearable
              multiple
              ref="age"
              :disabled="modes.includes('lmb')"
              :loading="isLoading && selectedField === 'age'"
              :success-messages="message.age"
              @change="toggleMenu('age')"
              @blur="saveOnChange('age')"
            >
              <template slot="selection" slot-scope="{ item, index }">
                <v-chip
                  :style="service === 'batch' ? tagStyles.age : getTagStyles('age', item)"
                  close
                  @[vChipAttributeName] = "handleChipRemoval({
                    index,
                    type: 'age',
                  })"
                >
                  <strong>
                    {{ item }}
                  </strong>&nbsp;
                </v-chip>
              </template>
            </v-autocomplete>
            <v-autocomplete
              :color="activeColor"
              class="select-overflow"
              v-model="tagType"
              :items="isProquestBulb ? tags.proquest : tags.subject"
              label="Subject"
              chips
              flat
              clearable
              multiple
              ref="subject"
              :disabled="modes.includes('lmb')"
              :loading="isLoading && selectedField === 'subject'"
              :success-messages="message.subject"
              @change="toggleMenu('subject')"
              @blur="saveOnChange(isProquestBulb ? 'proquest' : 'subject')"
            >
              <template slot="selection" slot-scope="{ item, index }">
                <v-chip
                  :style="service === 'batch' ? tagStyles.subject : getTagStyles('subject', item)"
                  close
                  @[vChipAttributeName] = "handleChipRemoval({
                    index,
                    type: isProquestBulb ? 'proquest' : 'subject',
                  })"
                >
                  <strong>
                    {{ item }}
                  </strong>&nbsp;
                </v-chip>
              </template>
            </v-autocomplete>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-header disable-icon-rotate>
              Playback options
              <template v-slot:actions>
                <v-icon color="primary">
                  play_circle
                </v-icon>
              </template>
            </v-expansion-panel-header>
            <v-expansion-panel-content
            :disabled="modes.includes('lmb')"
          >
            <template v-slot:header>
              <v-layout
                align-center
                justify-start
              >
                <v-icon left>
                  play_circle_outline
                </v-icon>
                Playback options
              </v-layout>
            </template>
              <v-checkbox
                label="Allow learners to rewind after an interaction appears"
                :color="activeColor"
                v-model="localBulb.config.rewind_yes"
                class="mb-3"
                :true-value="1"
                :false-value="0"
                :disabled="modes.includes('lmb')"
                @change="localBulb.apply_all.rewind_yes = 0"
                hide-details
              ></v-checkbox>
              <v-checkbox
                v-show="localBulb.config.rewind_yes !== originalBulb.config.rewind_yes"
                :color="activeColor"
                label="Apply this option to all existing interactions"
                v-model="localBulb.apply_all.rewind_yes"
                class="ml-4 small-label mb-3"
                :true-value="1"
                :false-value="0"
                :disabled="modes.includes('lmb')"
                hide-details
              ></v-checkbox>
              <v-checkbox
                :color="activeColor"
                label="Allow learners to skip interactions"
                v-model="localBulb.config.skip_yes"
                class="mb-3"
                :true-value="1"
                :false-value="0"
                :disabled="modes.includes('lmb')"
                @change="localBulb.apply_all.skip_yes = 0"
                hide-details
              ></v-checkbox>
              <v-checkbox
                v-show="localBulb.config.skip_yes !== originalBulb.config.skip_yes"
                :color="activeColor"
                label="Apply this option to all existing interactions"
                v-model="localBulb.apply_all.skip_yes"
                class="ml-4 small-label mb-3"
                :true-value="1"
                :false-value="0"
                :disabled="modes.includes('lmb')"
                hide-details
              ></v-checkbox>
              <v-checkbox
                :color="activeColor"
                label="Allow viewers to fast forward"
                v-model="localBulb.config.fast_yes"
                class="mb-3"
                :true-value="1"
                :false-value="0"
                :disabled="modes.includes('lmb')"
                hide-details
              ></v-checkbox>
              <v-checkbox
                :color="activeColor"
                :label="`Allow learners to retake the ${termForBulb} upon completion`"
                v-model="localBulb.config.retry_yes"
                class="mb-3"
                :true-value="1"
                :false-value="0"
                :disabled="modes.includes('lmb')"
                @change="handleBulbRetakeChange"
                hide-details
              ></v-checkbox>
              <v-text-field
                @blur="handleBulbRetakeLimitBlur(localBulb.config.attempts)"
                ref="bulbRetakeLimitInput"
                v-show="localBulb.config.retry_yes"
                v-model.number="localBulb.config.attempts"
                :color="activeColor"
                label="Number of retakes"
                type="number"
                class="ml-4 small-label mb-3"
                :min="0"
                :max="1000"
                :disabled="modes.includes('lmb')"
                :hint="`A value of 0 means that the ${termForBulb} can be retaken indefinitely.`"
                :persistent-hint="localBulb.config.attempts === 0"
              ></v-text-field>
              <v-checkbox
                @change="handlePrintableReportChange($event)"
                v-if="isSchoolTeacher"
                :color="activeColor"
                label="Allow learners to view their printable report"
                v-model="localBulb.config.export_yes"
                class="mb-3"
                :true-value="1"
                :false-value="0"
                :disabled="modes.includes('lmb')"
                hide-details
              ></v-checkbox>
              <v-checkbox
                @change="handleSubPrintableReportChange('enableViewReport', $event)"
                v-if="(
                  isSchoolTeacher
                  && localBulb.config.export_yes
                )"
                ref="enableViewReportCheckbox"
                :color="activeColor"
                label="Allow learners to view their report in a new tab"
                v-model="localBulb.config.enableViewReport"
                class="ml-4 small-label mb-3"
                :true-value="1"
                :false-value="0"
                :disabled="modes.includes('lmb')"
                hide-details
              ></v-checkbox>
              <v-checkbox
                @change="handleSubPrintableReportChange('enablePdfReport', $event)"
                v-if="(
                  isSchoolTeacher
                  && localBulb.config.export_yes
                  && permissionKeys.includes('showDownloadPdfBulbResultsButton')
                )"
                ref="enablePdfReportCheckbox"
                :color="activeColor"
                label="Allow learners to download their report in PDF format"
                v-model="localBulb.config.enablePdfReport"
                class="ml-4 small-label mb-3"
                :true-value="1"
                :false-value="0"
                :disabled="modes.includes('lmb')"
                hide-details
              ></v-checkbox>
              <v-checkbox
                @change="handleSubPrintableReportChange('enableHtmlReport', $event)"
                v-if="(
                  isSchoolTeacher
                  && localBulb.config.export_yes
                  && permissionKeys.includes('showDownloadHtmlBulbResultsButton')
                )"
                ref="enableHtmlReportCheckbox"
                :color="activeColor"
                label="Allow learners to download their report in HTML format"
                v-model="localBulb.config.enableHtmlReport"
                class="ml-4 small-label mb-3"
                :true-value="1"
                :false-value="0"
                :disabled="modes.includes('lmb')"
                hide-details
              ></v-checkbox>
              <v-checkbox
                @change="handleSubPrintableReportChange('enableDocxReport', $event)"
                v-if="(
                  isSchoolTeacher
                  && localBulb.config.export_yes
                  && permissionKeys.includes('showDownloadDocxBulbResultsButton')
                )"
                ref="enableDocxReportCheckbox"
                :color="activeColor"
                label="Allow learners to download their report in DOCX format"
                v-model="localBulb.config.enableDocxReport"
                class="ml-4 small-label mb-3"
                :true-value="1"
                :false-value="0"
                :disabled="modes.includes('lmb')"
                hide-details
              ></v-checkbox>
              <v-checkbox
                v-if="isPremiumOrSchoolTeacher"
                :color="activeColor"
                label="Allow learners to increase playback speed"
                v-model="localBulb.config.fast_playback"
                class="mb-3"
                :true-value="1"
                :false-value="0"
                :disabled="modes.includes('lmb')"
                hide-details
              ></v-checkbox>
              <v-checkbox
                v-if="isSchoolTeacher"
                :color="activeColor"
                label="Disable captions for this video"
                v-model="localBulb.config.captions_disabled"
                class="mb-3"
                :true-value="1"
                :false-value="0"
                :disabled="modes.includes('lmb')"
                hide-details
              ></v-checkbox>
              <v-checkbox
                :color="activeColor"
                :label="`Hide learner score upon ${termForBulb} completion`"
                v-model="localBulb.config.hide_learner_score"
                class="mb-3"
                :true-value="1"
                :false-value="0"
                :disabled="modes.includes('lmb')"
                hide-details
              ></v-checkbox>
              <Tooltip
                :attach="'.score-threshold-tooltip'"
                :disabled="isScoreThresholdEnabled"
                top>
                <div>
                  <span class="score-threshold-tooltip"></span>
                  <v-checkbox
                    :color="activeColor"
                    v-model="isScoreThresholdEnabled"
                    :disabled="!isScoreThresholdToggleEnabled"
                    hide-details
                    class="mb-3">
                    <template v-slot:label>
                      <div>
                        Do not mark {{termForBulb}} complete unless learner scores at least
                        <v-text-field
                          ref="scoreThresholdInput"
                          class="percentage-number-input ma-0 pa-0"
                          :color="activeColor"
                          @click.stop
                          @blur="handleScoreThresholdBlur(scoreThreshold)"
                          v-model.number="scoreThreshold"
                          type="number"
                          :disabled="!isScoreThresholdEnabled || !isScoreThresholdToggleEnabled"
                          :placeholder="scoreThresholdPlaceholder"
                          :min="1"
                          :max="100"
                          hide-details
                          :error-messages="scoreThresholdError"
                        ></v-text-field>
                      </div>
                    </template>
                  </v-checkbox>
                  <div
                    v-if="isScoreThresholdToggleEnabled"
                    class="caption grey--text indented-text mb-2">
                    {{ scoreThresholdString }}
                  </div>
                </div>
                <template v-slot:text>
                  <span class="white--text">
                    {{ scoreThresholdPlaylistTooltipMessage }}
                  </span>
                </template>
              </Tooltip>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-header disable-icon-rotate>
              Privacy
              <template v-slot:actions>
                <v-icon color="primary">
                  key
                </v-icon>
              </template>
            </v-expansion-panel-header>
            <v-expansion-panel-content
            :disabled="modes.includes('lmb')"
          >
            <template v-slot:header>
              <v-layout
                align-center
                justify-start
              >
                <v-icon left>
                  vpn_key
                </v-icon>
                Privacy
              </v-layout>
            </template>
              <v-radio-group
                v-model="localBulb.privacy"
                :color="activeColor"
                :disabled="modes.includes('lmb')"
                :hide-details="!message.privacy"
                :success-messages="message.privacy"
              >
                <v-radio
                  :color="activeColor"
                  :value="1"
                >
                  <template slot="label">
                    <v-list class="privacyLabelStyle" :two-line="isProquestBulb">
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title>
                            <v-icon v-if="this.isProquestBulb" size=24 class="pr-2">
                              visibility
                            </v-icon>
                            {{ this.privacyLabels.public }}
                          </v-list-item-title>
                          <v-list-item-subtitle v-if="this.isProquestBulb">
                            Everyone can share or copy your {{termForBulb}}.
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </template>
                </v-radio>
                <v-radio
                  v-if="!isProquestBulb"
                  :color="activeColor"
                  :disabled="!isSchoolTeacher"
                  :value="2"
                >
                  <template slot="label">
                    <v-list class="privacyLabelStyle" :two-line="isProquestBulb">
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title>
                            {{ this.privacyLabels.district }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </template>
                </v-radio>
                <v-radio
                  :color="activeColor"
                  :disabled="!isSchoolTeacher"
                  :value="3"
                >
                  <template slot="label">
                    <v-list class="privacyLabelStyle" :two-line="isProquestBulb">
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title class="text-wrap">
                            <v-icon v-if="this.isProquestBulb" size=24 class="pr-2">
                              account_balance
                            </v-icon>
                            {{ this.privacyLabels.school }}
                          </v-list-item-title>
                          <v-list-item-subtitle v-if="this.isProquestBulb">
                            Anyone within your institution can share or copy your {{termForBulb}}.
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </template>
                </v-radio>
                <v-radio
                  :color="activeColor"
                  :value="5"
                >
                  <template slot="label">
                    <v-list class="privacyLabelStyle" :two-line="isProquestBulb">
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title>
                            <v-icon v-if="this.isProquestBulb" size=24 class="pr-2">
                              visibility_off
                            </v-icon>
                            {{ this.privacyLabels.private }}
                          </v-list-item-title>
                          <v-list-item-subtitle v-if="this.isProquestBulb">
                            Only you can share or copy your {{termForBulb}}.
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </template>
                </v-radio>
              </v-radio-group>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- Container that saves all the actionable buttons: -->
        <div class="save-container">
          <v-card-actions
            v-if="(
              service === 'dashboard'
              || service === 'playlist'
            )"
            class="row-space width-100"
          >
            <v-btn
              v-bind="saveActionsButtonProps"
              class="branded-grey"
              @click="$emit('cancel')"
            >
              Cancel
            </v-btn>
            <v-btn
              class="branded-background text-white"
              :color="(['playlist', 'designer'].includes(service)) ? 'primary' : ''"
              :disabled="!localBulb.name || !haveChangesBeenMade"
              @click="formatDataForSave()"
            >
              Save
            </v-btn>
          </v-card-actions>
          <v-card-actions
            v-else-if="service === 'batch'"
            class="row-space width-100"
          >
            <v-layout
              class="my-2"
              :justify-end="!haveChangesBeenMade"
              :justify-space-between="haveChangesBeenMade"
            >
              <v-btn
                v-if="haveChangesBeenMade"
                v-bind="saveActionsButtonProps"
                class="branded-grey"
                @click="cancelChanges"
              >
                Undo changes
              </v-btn>
              <v-btn
                class="branded-background text-white"
                :disabled="!localBulb.name || !haveChangesBeenMade"
                @click="formatDataForSave()"
              >
                Save
              </v-btn>
            </v-layout>
          </v-card-actions>
          <v-card-actions
            v-else-if="service === 'batchApplyToAll'"
            class="row-space width-100"
          >
            <v-layout
              class="my-2"
              :justify-end="!haveChangesBeenMade"
              :justify-space-between="haveChangesBeenMade"
            >
              <v-btn
                v-if="haveChangesBeenMade"
                flat
                class="branded-grey"
                @click="cancelChanges"
              >
                Undo changes
              </v-btn>
              <v-btn
                class="branded-background text-white"
                :disabled="!haveChangesBeenMade"
                @click="formatDataForSave()"
              >
                Apply
              </v-btn>
            </v-layout>
          </v-card-actions>
          <v-card-actions
            v-else
            class="width-100"
          >
          <v-layout
              v-if="isProquestBulb"
              justify-space-between
            >
              <v-btn
                id="playposit-bulb-settings-cancel-button"
                depressed
                v-bind="saveActionsButtonProps"
                @click="cancelChanges"
                class="mr-2"
              >
                Reset Form
              </v-btn>
              <v-btn
                id="playposit-bulb-settings-save-button"
                depressed
                :color="activeColor"
                :disabled="(
                  !isFormValid
                  || isProcessing
                  || isSaveAndExitButtonDisabled
                )"
                @click="handleProquestSave"
                class="mr-0"
              >
                <span :style="{color: saveChangesTextColor}">Save and Exit</span>
              </v-btn>
            </v-layout>
            <v-layout
              v-else-if="!haveChangesBeenMade"
              justify-end
            >
              <v-btn
                v-if="!isProquestBulb"
                id="playposit-bulb-settings-preview-button"
                depressed
                v-bind="saveActionsButtonProps"
                @click="$emit('preview')"
                class="mr-2"
              >
                Preview
              </v-btn>
              <v-btn
                id="playposit-bulb-settings-submit-button"
                :disabled="isSaveAndExitButtonDisabled"
                depressed
                color="primary"
                @click="$emit('exit')"
                class="mr-0"
              >
                <template v-if="modes.includes('lms')">
                  Save
                </template>
                <template v-else-if="modes.includes('lmb')">
                  Submit
                </template>
                <template v-else>
                  Save and exit
                </template>
              </v-btn>
            </v-layout>
            <v-layout
              v-else
              justify-space-between
            >
              <v-btn
                id="playposit-bulb-settings-cancel-button"
                depressed
                :flat="!['playlist', 'designer'].includes(service)"
                :text="['playlist', 'designer'].includes(service)"
                @click="cancelChanges"
                class="mr-2"
              >
                Cancel
              </v-btn>
              <v-btn
                id="playposit-bulb-settings-save-button"
                depressed
                :color="activeColor"
                :disabled="!isFormValid || isProcessing"
                @click="formatDataForSave()"
                class="mr-0"
              >
                <span :style="{color: saveChangesTextColor}">Save changes</span>
              </v-btn>
            </v-layout>
          </v-card-actions>

          <!-- Heading specific to the 'batch' service: -->
          <hr
            v-if="(
              service === 'batch'
              && !isLastBulbInBatch
            )"
            class="width-100 light-gray-border"
          />
        </div>

      </v-layout>
    </v-form>
  </v-card>
</template>

<script>
import sanitizeHtml from 'sanitize-html';

/* global _ */
import {
  mapState,
  mapActions,
} from 'vuex';
import { DateTime } from 'luxon';

import Color from 'color';
import axios from 'axios';

import {
  calculateContrast,
} from '@utilities'; // eslint-disable-line

import bannedTags from './js/bannedTags';

// Global mixin(s):
import Toast from '@mixins/Toast/Toast.js'; // eslint-disable-line

const defaultBulb = {
  keywords: [],
  age: [],
  subject: [],
  proquest: [],
  tags: [],
  config: {
    rewind_yes: 0,
    skip_yes: 0,
    fast_yes: 0,
    retry_yes: 0,
    attempts: 0,
    export_yes: 0,
    mark_yes: 0,
    fast_playback: 1,
  },
  apply_all: {
    rewind_yes: 0,
    skip_yes: 0,
    fast_yes: 0,
    retry_yes: 0,
    export_yes: 0,
    mark_yes: 0,
    fast_playback: 1,
  },
};

export default {
  name: 'PlaypositBulbSettings',
  props: {
    wrapperSize: {
      type: Number,
      default: 500,
    },
    bulb: {
      type: Object,
      default: () => {},
    },
    moduleBulb: { // needed specifically for the playlist flow for setting score threshold
      type: Object,
      default: () => {},
    },
    modes: {
      type: Array,
      default: () => [],
    },
    service: {
      type: String,
      default: 'designer',
      validator: (service) => [
        'batch',
        'batchApplyToAll',
        'dashboard',
        'designer',
        'playlist',
      ].includes(service),
    },
    userType: {
      type: String,
      validator: (userType) => [
        'freeTeacher',
        'premiumTeacher',
        'schoolTeacher',
        'schoolAdmin',
      ].includes(userType),
    },
    tags: {
      type: Object,
      default: () => ({}),
    },
    schoolListed: {
      type: Number || Boolean,
    },
    saveBulbSettings: {
      type: Function,
    },
    school: {
      type: Object,
    },
    permissionKeys: {
      type: Array,
      default: () => ([]),
    },
    activeColor: {
      type: String,
      default: '#2196F3',
    },
    apiUrl: {
      type: String,
      default: '/',
    },
    token: String,
    selectedBulbSettings: Number,
    isLastBulbInBatch: Boolean,
    pointsAvailable: Number,
    timezone: String,
  },
  mixins: [
    Toast,
  ],
  data: () => ({
    localBulb: defaultBulb,
    originalBulb: defaultBulb,
    haveChangesBeenMade: false,
    isFormValid: true,
    isUpdating: false,
    isProcessing: false,
    isProquestBulb: false,
    bulbInteractions: [],
    hasRevertedScoreThreshold: false,
    message: {
      name: '',
      learningObjective: '',
      keywords: '',
      age: '',
      subject: '',
    },
    referenceBulb: {},
    selectedField: null,
    saving: false,
    formErrors: [],
    rules: {
      name: [
        (name) => !!(name && name.length) || 'This field is required.',
      ],
      tags: [
        (tags) => tags.every((tag) => bannedTags.indexOf(tag) === -1) || 'Inappropriate tags are not allowed',
        (tags) => tags.every((tag) => tag.length <= 255) || 'Tags must be less than 255 characters',
      ],
    },
    tagMatchesSearched: false,
    tagSearchString: null,
    titleInputKey: 1,
    scoreThresholdPlaceholder: '100',
    isScoreThresholdEnabled: false,
    playlistBulbSettingsSlideoutOpened: false,
    openedExpansionPanel: null,
    previousBulbCheckoutStudentId: null,
    checkoutDateMenu: false,
    checkoutTimeMenu: false,
    isLoadingStudentEmail: false,
    checkoutDetails: {
      action: null,
      studentId: '',
      studentEmail: '',
      duration: 0,
      checkoutDate: '',
      checkoutTime: '',
    },
    originalCheckoutDetails: {
      action: null,
      studentId: '',
      studentEmail: '',
      duration: 0,
      checkoutDate: '',
      checkoutTime: '',
    },
  }),
  computed: {
    ...mapState([
      'searchingForTags',
    ]),
    api() {
      return {
        getStudentDetails: (studentId) => axios.get(`${this.url}learner/details/${studentId}`),
        getBulbProquestStatus: (bulbId) => axios.get(`${this.url}bulb/${bulbId}/isProquest`),
        getBulbInteractions: (bulbId) => axios.get(`${this.url}bulb/${bulbId}/interaction`),
        sendProquestData: () => axios.post(`${this.url}proquest/data/bulb/${this.bulb.id}/update`),
        init: () => {
          axios.defaults.headers.common.Authorization = `Bearer ${this.token}`;
          axios.defaults.headers.common['Content-Type'] = 'application/x-www-form-urlencoded';
          axios.defaults.headers.common.Accept = 'application/json';
        },
      };
    },
    isSaveAndExitButtonDisabled() {
      return (
        this.isProquestBulb
        && !this.localBulb?.name?.length
      );
    },
    termForBulb() {
      return this.isProquestBulb ? 'interaction' : 'bulb';
    },
    isSearching() {
      return this.selectedBulbSettings === this.localBulb.id && this.searchingForTags;
    },
    isLoading() {
      return this.isUpdating;
    },
    isSchoolTeacher() {
      return [
        'schoolTeacher',
        'schoolAdmin',
      ].includes(this.userType);
    },
    isPremiumOrSchoolTeacher() {
      return (
        this.isSchoolTeacher
        || this.userType === 'premiumTeacher'
      );
    },
    hasThumbnail() {
      return (
        this.localBulb.thumbnail
        && this.localBulb.thumbnail.length
      );
    },
    uniqueTagTypes() {
      return _.uniqBy(_.get(this, 'tags.rawData', []), 'type');
    },
    hasTagErrors() {
      return (
        this.formErrors.includes('keywords')
        || this.formErrors.includes('age')
        || this.formErrors.includes('subject')
      );
    },
    sanitizedVideoName: {
      get() {
        return this.localBulb.name;
      },
      set(value) {
        this.localBulb.name = sanitizeHtml(value)
          .replace(/&amp;/g, "&")
          .replace(/&nbsp;/g, " ")
          .replace(/&lt;3/g, "<3");
      }
    },
    tagStyles() {
      const tagStyles = {};
      for (const tag of this.uniqueTagTypes) {
        tagStyles[tag.type] = {
          backgroundColor: tag.hex,
          color: `${Color(tag.hex).darken(0.6)} !important`,
          border: `1px solid ${Color(tag.hex).darken(0.6)} !important`,
        };
      }
      return tagStyles;
    },
    tagType: {
      get() {
        return this.isProquestBulb ? this.localBulb.proquest : this.localBulb.subject;
      },
      set(newValue) {
        if (this.isProquestBulb) this.localBulb.proquest = newValue;
        else this.localBulb.subject = newValue;
      },
    },
    localSelectedBulbSettings: {
      get() {
        return this.selectedBulbSettings;
      },
      set(value) {
        this.$emit('update:selected-bulb-settings', value);
      },
    },
    scoreThreshold: {
      get() {
        return this.localBulb.config.scoreThreshold;
      },
      set(value) {
        this.$set(this.localBulb.config, 'scoreThreshold', value);
      },
    },
    scoreThresholdString() {
      const points = this?.pointsAvailable ?? this?.localBulb?.pointsAvailable;
      const scoreThreshold = _.isFinite(this.scoreThreshold) ? this.scoreThreshold : Number(this.scoreThresholdPlaceholder);
      /*
        Having this "+" before the next line makes it so that the number is only displayed as a decimal with two digit places if it was a decimal originally. So 6.12345 would become 6.12 but 6 would still remain as 6, instead of becoming 6.00.
      */
      const pointsThreshold = Math.ceil((scoreThreshold / 100) * points);
      return `Learners will need to earn at least ${pointsThreshold} of the ${points} available point${points === 1 ? '' : 's'} on this ${this.termForBulb}.`;
    },
    scoreThresholdError() {
      if (
        this.isScoreThresholdEnabled
        && !_.isFinite(this.scoreThreshold)
      ) return ['Score threshold must be a valid number between 1 and 100.'];
      return [];
    },
    saveChangesTextColor() {
      return calculateContrast(Color(this.activeColor).hex());
    },
    url() {
      return this.apiUrl[this.apiUrl.length - 1] === '/' ? this.apiUrl : `${this.apiUrl}/`;
    },
    isBulbCheckoutEnabled() {
      return this?.permissionKeys?.includes('displayBulbSettingsBulbCheckoutPanel');
    },
    bulbCheckoutActions() {
      return [
        {
          text: '-',
          value: null,
        },
        {
          text: 'Update',
          value: 'update',
          disabled: !this.checkoutDetails.enabled,
          description: 'Updates any of the following values that are provided: learner email address, checkout duration, and the checkout date/time.',
        },
        {
          text: 'Enable',
          value: 'enable',
          disabled: this.checkoutDetails.enabled,
          description: `Enables checkout functionality for this ${this.termForBulb}. If a student email is provided, that student will check the ${this.termForBulb} out. If a student email is not provided, the first student to begin the ${this.termForBulb} will check it out. If no date/time is provided, the current date/time will be used.`,
        },
        {
          text: 'Disable',
          value: 'disable',
          disabled: !this.checkoutDetails.enabled,
          description: `Turns off the checkout functionality for this ${this.termForBulb}.`,
        },
        {
          text: 'Lock',
          value: 'lock',
          disabled: !this.checkoutDetails.enabled,
          description: `Sets this ${this.termForBulb}'s checkout time to the current time, meaning that it will be unavailable until (the current time + the checkout duration).`,
        },
        {
          text: 'Unlock',
          value: 'unlock',
          disabled: !this.checkoutDetails.enabled,
          description: `Resets this ${this.termForBulb}'s checkout time so that it can be checked out again.`,
        },
      ];
    },
    areBulbCheckoutFieldsEnabled() {
      return [
        'update',
        'enable',
      ].includes(this.checkoutDetails.action);
    },
    timePickerMinimumTime() {
      if (this.checkoutDetails.checkoutDate !== new Date().toISOString().split('T')[0]) return '';
      return DateTime.now().setZone(this.timezone).toISO().split('T')[1].split('.')[0];
    },
    formattedCheckoutTime() {
      if (!this.checkoutDetails?.checkoutTime) return null;
      return DateTime.fromISO(this.checkoutDetails.checkoutTime).setZone(this.timezone).toLocaleString(DateTime.TIME_WITH_SECONDS);
    },
    checkoutDateTimeInUtc() {
      if (
        !this.checkoutDetails?.checkoutDate
        || !this.checkoutDetails?.checkoutTime
      ) return null;
      return DateTime.fromISO(`${this.checkoutDetails?.checkoutDate}T${this.checkoutDetails?.checkoutTime}`, {
        zone: this.timezone,
      }).toUTC();
    },
    saveActionsButtonProps() {
      if (['playlist', 'designer'].includes(this.service)) {
        return {
          text: true,
          outlined: true,
        };
      }
      return {
        flat: true,
        outline: true,
      };
    },

    vChipAttributeName() {
      if (['playlist', 'designer'].includes(this.service)) return 'click:close';
      return 'input';
    },
    scoreThresholdPlaylistTooltipMessage() {
      if (this.isScoreThresholdEnabled) return '';
      if (!this.isScoreThresholdToggleEnabled) return `Need one interaction worth points, retakes enabled, and a percentage threshold to mark ${this.termForBulb} complete.`;
      const tooltipMessageMap = {
        playlist: 'Adjusting the minimum completion score within the Playlist will not impact the minimum completion score set outside of the Playlist.',
        // possibly any more? Depending on which microservice, future or past, will determine this.
      };
      return tooltipMessageMap[this.service];
    },
    privacyLabels() {
      if (this.isProquestBulb) {
        return {
          public: 'Public - Visible to everyone',
          district: 'Editorial',
          school: 'Institutional - Visible to anyone within your institution',
          private: 'Private - Only visible to you',
        };
      }
      return {
        public: `${this.termForBulb.capitalize()} is public (visible in Premade ${this.termForBulb.capitalize()}s & My Community)`,
        district: `${this.termForBulb.capitalize()} is visible to members of your district/institution`,
        school: `${this.termForBulb.capitalize()} is visible to members of your school/site.`,
        private: `${this.termForBulb.capitalize()} is private (not visible in Premade ${this.termForBulb.capitalize()}s & My Community)`,
      };
    },
  },
  asyncComputed: {
    isScoreThresholdToggleEnabled: {
      async get() {
        if (
          ['playlist'].includes(this.service)
          && !this.playlistBulbSettingsSlideoutOpened
        ) {
          return; // going to call later, when the playlist bulb settings slideout opens
        }
        await this.getBulbInteractions();
        // Checks for the case if a score threshold was initially set, but then the interactions were removed:
        if (
          !this.bulbInteractions.length
          && this?.localBulb?.config?.scoreThreshold
        ) {
          this.resetScoreThreshold();
          await this.$nextTick();
          if (this.haveChangesBeenMade) {
            this.setHasRevertedScoreThresholdProperty();
            await this.$nextTick();
            this.fireToast({
              icon: 'info',
              iconColor: this.$vuetify.theme.primary,
              position: 'top-right',
              title: `This bulb no longer has a score threshold set since all interactions worth points have been removed.`,
              timer: 7000,
            });
          }
          return false;
        }
        const includeModeConditional = ['dashboard', 'designer'].includes(this.service);
        return (
          this.pointsAvailable > 0
          && !!this.localBulb.config?.retry_yes
          && (
            includeModeConditional // some areas in our code don't need this check for bulb settings
            || !this.modes.includes('lmb')
          )
        )
      },
      watch: ['localBulb.config.retry_yes', 'pointsAvailable', 'haveChangesBeenMade'],
    },
  },
  methods: {
    ...mapActions([
      'getTags',
    ]),
    handleBulbRetakeLimitBlur(value) {
      if (
        value < 0
        || !_.isFinite(parseInt(value, 10))
      ) {
        _.set(this, 'localBulb.config.attempts', 0);
        this.$refs.bulbRetakeLimitInput.internalValue = 0;
      } else if (value > 1000) {
        _.set(this, 'localBulb.config.attempts', 1000);
        this.$refs.bulbRetakeLimitInput.internalValue = 1000;
      } else if (!Number.isInteger(value)) {
        _.set(this, 'localBulb.config.attempts', Math.trunc(value));
        this.$refs.bulbRetakeLimitInput.internalValue = Math.trunc(value);
      }
    },
    async handleProquestSave() {
      await this.formatDataForSave();
      this.$emit('exit');
    },
    handleDisableCaptionsChange() {
      if (
        this.localBulb.config?.enable_transcript
        && !this.localBulb.config.captions_disabled
      ) this.localBulb.config.enable_transcript = 0;
    },
    handlePrintableReportChange(value) {
      this.localBulb.apply_all.export_yes = 0;
      if (value) {
        this.localBulb.config.enableViewReport = 1;
        this.localBulb.config.enablePdfReport = 0;
        this.localBulb.config.enableHtmlReport = 0;
        this.localBulb.config.enableDocxReport = 0;
      } else {
        this.localBulb.config.enableViewReport = 0;
        this.localBulb.config.enablePdfReport = 0;
        this.localBulb.config.enableHtmlReport = 0;
        this.localBulb.config.enableDocxReport = 0;
      }
    },
    async handleSubPrintableReportChange(key, value) {
      if (value) return;
      await this.$nextTick();
      /*
        One of these four values MUST be checked if `localBulb.config.export_yes` is true so if a user tries to uncheck the last checked box out of these four, we simply re-check it since Vuetify's support for similar functionality in their radio buttons or checkboxes wasn't too great.
      */
      if (
        !this.localBulb.config.enableViewReport
        && !this.localBulb.config.enablePdfReport
        && !this.localBulb.config.enableHtmlReport
        && !this.localBulb.config.enableDocxReport
      ) {
        this.$set(this.localBulb.config, key, 1);
      }
    },
    handleScoreThresholdBlur(value) {
      if (value < 0 || !_.isFinite(parseInt(value, 10))) {
        _.set(this, 'localBulb.config.scoreThreshold', 1);
        this.scoreThresholdPlaceholder = '1';
      } else if (value > 100) {
        _.set(this, 'localBulb.config.scoreThreshold', 100);
        this.scoreThresholdPlaceholder = '100';
      } else if (!Number.isInteger(value)) {
        _.set(this, 'localBulb.config.scoreThreshold', Math.trunc(value));
        this.scoreThresholdPlaceholder = Math.trunc(value).toString();
      }
    },
    handleBulbRetakeChange(canRetake) {
      this.localBulb.apply_all.retry_yes = 0;
      if (!canRetake) {
        this.$set(this.localBulb.config, 'attempts', 0);
      }
    },
    handleTextPasted(event) {
      event.preventDefault()
      const contents = event.clipboardData.getData('text');
      this.sanitizedVideoName = contents;
      // The text input field needs to be manually cycled on paste to update the sanitized text
      this.titleInputKey += 1;
    },
    toggleMenu(type) {
      if (
        this.localBulb[type].length
        && this.$refs[type]
      ) {
        this.$refs[type].blur();
      }
    },
    handleChipRemoval({
      index,
      type,
    } = {}) {
      if (type === 'keywords') this.localBulb.keywords.splice(index, 1);
      else this.$delete(this.localBulb[type], index);
      this.saveOnChange(type);
    },
    cancelChanges() {
      this.$set(this, 'localBulb', _.cloneDeep(this.originalBulb));
      this.$set(this, 'checkoutDetails', _.cloneDeep(this.originalCheckoutDetails));
    },
    clearTagSearchString() {
      this.$set(this, 'tagSearchString', '');
    },
    getTagStyles(key, item) {
      if (
        bannedTags.includes(item.toLowerCase())
        || item.length >= 255
      ) {
        return {
          backgroundColor: '#EF5350',
          color: 'white !important',
          border: '1px solid #602120 !important',
        };
      }
      return this.tagStyles[key];
    },
    async saveOnChange(field) {
      try {
        if (
          this.service !== 'batch'
          || JSON.stringify(_.get(this, `originalBulb.${field}`)) === JSON.stringify(_.get(this, `localBulb.${field}`))
          || this.isUpdating
        ) return;
        this.selectedField = field;
        this.isUpdating = true;
        await this.formatDataForSave(null, { isAutoSave: true });
        this.isUpdating = false;
        this.$emit('snackbar', {
          text: 'Tags saved.',
          color: 'success',
          timeout: 3000,
        });
        this.$set(this.message, field, 'Successfully updated.');
        await this.$timer(3000);
        this.$set(this.message, field, '');
      } catch(e) {
        /* eslint no-console: "off" */
        console.error(e.message);
      }
    },
    setSelectedField(field) {
      if (this.service === 'batch') this.selectedField = field;
    },
    constructFinalCheckoutDetails() {
      if ([
        'enable',
        'update',
      ].includes(this.checkoutDetails.action)) {
        const details = {};
        if (this.checkoutDetails.action) details.action = this.checkoutDetails.action;
        if (this.checkoutDetails.duration) details.duration = parseInt(this.checkoutDetails.duration, 10);
        if (this.checkoutDateTimeInUtc) details.time = this.checkoutDateTimeInUtc?.ts;
        if (this.checkoutDetails.studentEmail) details.studentEmail = this.checkoutDetails.studentEmail;
        return details;
      }
      return { action: this.checkoutDetails.action };
    },
    async sendProquestData() {
      try {
        if (
          !this.isProquestBulb
          || this.service === 'designer'
        ) return;
        const { data } = await this.api.sendProquestData();
        if (
          data?.id
          && window.self !== window.top
        ) {
          window.parent.postMessage(JSON.stringify({
            type: 'bulb',
            event: 'update',
            data,
          }), '*');
        }
      } catch(e) {
        return e;
      }
    },
    async formatDataForSave(cb, {
      applyToAllBulb,
      isAutoSave,
    } = {}) {
      const bulb = {
        bulb_id: this.localBulb.id,
        name: this.localBulb.name,
        learning_objective: this.localBulb.learningObjective,
        thumbnail: this.localBulb.thumbnail,
        privacy: applyToAllBulb?.privacy ?? this.localBulb.privacy,
        config: {
          ...this.localBulb.config,
          ...(applyToAllBulb?.config ?? {})
        },
        apply_all: applyToAllBulb?.apply_all ?? this.localBulb.apply_all,
        add_tags: applyToAllBulb?.add_tags ?? [],
        delete_tags: applyToAllBulb?.delete_tags ?? [],
        checkoutDetails: this.constructFinalCheckoutDetails(),
      };
      if (!applyToAllBulb) {
        for (const tag of this.referenceBulb.tags) {
          if (
            tag.type === 'user-generated'
            && this.localBulb.keywords.indexOf(tag.text) < 0
          ) bulb.delete_tags.push(tag.id);

          if (
            tag.type === 'age'
            && this.localBulb.age.indexOf(tag.text) < 0
          ) bulb.delete_tags.push(tag.id);

          if (
            tag.type === 'subject'
            && this.localBulb.subject.indexOf(tag.text) < 0
          ) bulb.delete_tags.push(tag.id);

          if (
            tag.type === 'proquest'
            && this.localBulb.proquest.indexOf(tag.text) < 0
          ) bulb.delete_tags.push(tag.id);
        }
      }
      if (!applyToAllBulb) {
        for (const keyword of this.localBulb.keywords) {
          if (this.originalBulb.keywords.indexOf(keyword.trim()) >= 0) continue;
          bulb.add_tags.push({
            bulbId: this.localBulb.id,
            tagId: 1,
            text: keyword.trim(),
          });
        }
        for (const ageTag of this.localBulb.age) {
          if (this.originalBulb.age.indexOf(ageTag) >= 0) continue;
          for (const tag of this.tags.rawData) {
            if (
              tag.type === 'age'
              && tag.value === ageTag
            ) {
              bulb.add_tags.push({
                bulbId: this.localBulb.id,
                tagId: tag.id,
              });
            }
          }
        }
        for (const subjectTag of this.localBulb.subject) {
          if (this.originalBulb.subject.indexOf(subjectTag) >= 0) continue;
          for (const tag of this.tags.rawData) {
            if (
              tag.type === 'subject'
              && tag.value === subjectTag
            ) {
              bulb.add_tags.push({
                bulbId: this.localBulb.id,
                tagId: tag.id,
              });
            }
          }
        }
        for (const proquestTag of this.localBulb.proquest) {
          if (this.originalBulb.proquest.indexOf(proquestTag) >= 0) continue;
          for (const tag of this.tags.rawData) {
            if (
              tag.type === 'proquest'
              && tag.value === proquestTag
            ) {
              bulb.add_tags.push({
                bulbId: this.localBulb.id,
                tagId: tag.id,
              });
            }
          }
        }
      }
      if (
        (
          this.service === 'playlist'
          && // eslint-disable-line
          (
            this.scoreThreshold
            || this.scoreThreshold === null
          )
        )
      ) {
        this.$emit('scorethreshold-playlist-save', {
          scoreThreshold: this.scoreThreshold,
        });
        delete this.localBulb.config?.scoreThreshold;
      }
      if (this.service === 'batchApplyToAll') {
        return this.$emit('apply-to-all', bulb);
      }
      const updatedBulbData = await this.saveBulbSettings(bulb, cb);
      this.sendProquestData();
      this.scoreThresholdPlaceholder = '100';
      this.referenceBulb.tags = [
        ...(
          _.has(updatedBulbData, 'tagData.data')
          ? updatedBulbData.tagData.data // eslint-disable-line
          : _.get(updatedBulbData, 'tags') // eslint-disable-line
        ) // eslint-disable-line
      ];
      if (!_.isEmpty(updatedBulbData?.config?.checkout)) {
        this.localBulb.config.checkout = updatedBulbData.config.checkout;
      } else if (!_.isEmpty(updatedBulbData?.bulbData?.data?.config?.checkout)) {
        this.localBulb.config.checkout = updatedBulbData.bulbData.data.config.checkout;
      }
      this.initBulbCheckout();
      await this.$set(this, 'originalBulb', _.cloneDeep(this.localBulb));
      if (this.service === 'batch') {
        this.haveChangesBeenMade = !_.isEqual(this.localBulb, this.originalBulb);
        if (
          !isAutoSave
          && !applyToAllBulb
        ) {
          this.$emit('snackbar', {
            text: `"${this.localBulb.name}" has been successfully updated!`,
            color: 'success',
            timeout: 3000,
          });
        }
      }
      if (!_.isEmpty(applyToAllBulb)) {
        this.$set(this.localBulb, 'tags', this.referenceBulb.tags);
        this.initBulb({ bulb: this.localBulb });
      }
    },
    async applyBulb(applyBulb) {
      /*
        We only want settings that have been deliberately enabled in the Apply to All tab to be applied to each individual bulb. Here we are filtering out any non-positive playback setting so that checkboxes that are unchecked within the Apply to All tab when Apply is clicked do not overwrite those same checkboxes for each individual bulb.
      */
      for (const key in applyBulb?.config) {
        if (!applyBulb.config[key]) {
          delete applyBulb.config[key];
        }
      }
      const bulb = {
        id: this.localBulb.id,
        bulb_id: this.localBulb.id,
        name: this.localBulb.name,
        learning_objective: this.localBulb.learningObjective,
        thumbnail: this.localBulb.thumbnail,
        privacy: applyBulb?.privacy ?? this.localBulb.privacy,
        config: {
          ...this.localBulb.config,
          ...applyBulb?.config,
        },
        apply_all: applyBulb?.apply_all ?? this.localBulb.apply_all,
        add_tags: applyBulb?.add_tags ?? [],
        delete_tags: applyBulb?.delete_tags ?? [],
        checkoutDetails: this.constructFinalCheckoutDetails(),
      };
      if (bulb.add_tags?.length) {
        bulb.add_tags = bulb.add_tags.map((addTag) => ({
          ...addTag,
          bulbId: this.localBulb.id,
        }))
      }
      if (bulb.delete_tags?.length) {
        bulb.delete_tags = bulb.delete_tags.map((deleteTag) => ({
          ...deleteTag,
          bulbId: this.localBulb.id,
        }))
      }
      this.initBulb({ bulb });
      await this.formatDataForSave(null, { applyToAllBulb: bulb });
      return bulb;
    },
    handlePostApplyToAll({
      success,
      error,
      reset,
    } = {}) {
      if (reset) this.cancelChanges();
      if (success) {
        this.$emit('snackbar', {
          text: 'These settings have been successfully applied to all selected bulbs.',
          color: 'success',
          timeout: 5000,
        });
      } else if (error) {
        this.$emit('snackbar', {
          text: 'Unable to apply settings to all selected bulbs at this time.',
          color: 'success',
          timeout: 3000,
        });
      }
    },
    initBulb({
      bulb,
      save,
      callback,
    } = {}) {
      if (!bulb) bulb = this.bulb;
      this.$set(this, 'referenceBulb', _.cloneDeep(bulb));
      if (this.service === 'batch') {
        this.localBulb = {
          config: {
            rewind_yes: 0,
            skip_yes: 0,
            fast_yes: 0,
            retry_yes: 0,
            attempts: 0,
            export_yes: 0,
            mark_yes: 0,
            fast_playback: this.modes.includes('lmb') ? 0 : 1,
          },
          apply_all: {
            rewind_yes: 0,
            skip_yes: 0,
            fast_yes: 0,
            retry_yes: 0,
            export_yes: 0,
            mark_yes: 0,
            fast_playback: this.modes.includes('lmb') ? 0 : 1,
          },
        };
      }
      if (!this.referenceBulb.config) this.referenceBulb.config = {};
      this.localBulb.keywords = [];
      this.localBulb.age = [];
      this.localBulb.subject = [];
      this.localBulb.proquest = [];
      if ( // This is needed to not get bulb level and playlist level scoreThresholds intertwined
        this.service === 'playlist'
        && _.get(this.referenceBulb, 'config.scoreThreshold', false)
      ) delete this.referenceBulb.config.scoreThreshold;
      // eslint-disable-next-line guard-for-in
      for (const key in this.referenceBulb) {
        this.$set(this.localBulb, key, this.referenceBulb[key]);
        if (key === 'learning_objective') {
          this.$set(this.localBulb, 'learningObjective', this.referenceBulb.learning_objective);
        }
        if (key === 'tags') {
          for (const tag of this.referenceBulb[key]) {
            if (
              tag.type === 'user-generated'
              && !this.localBulb.keywords.includes(tag.text)
            ) this.localBulb.keywords.push(tag.text);
            if (
              tag.type === 'age'
              && !this.localBulb.age.includes(tag.text)
            ) this.localBulb.age.push(tag.text);
            if (
              tag.type === 'subject'
              && !this.localBulb.subject.includes(tag.text)
            ) this.localBulb.subject.push(tag.text);
            if (
              tag.type === 'proquest'
              && !this.localBulb.proquest.includes(tag.text)
            ) this.localBulb.proquest.push(tag.text);
          }
        }
      }
      const scoreThresholdReference = (this.service === 'playlist')
        ? _.get(this, 'moduleBulb.config.scoreThreshold')
        : _.get(this, 'localBulb.config.scoreThreshold');
      this.isScoreThresholdEnabled = _.isFinite(scoreThresholdReference);
      if (this.isScoreThresholdEnabled) {
        this.scoreThresholdPlaceholder = `${scoreThresholdReference}`;
      }
      if (
        this.localBulb.config.export_yes
        && (
          !_.has(this.localBulb.config, 'enableViewReport')
          && !_.has(this.localBulb.config, 'enablePdfReport')
          && !_.has(this.localBulb.config, 'enableHtmlReport')
          && !_.has(this.localBulb.config, 'enableDocxReport')
        )
      ) {
        this.$set(this.localBulb.config, 'enableViewReport', 1);
        this.$set(this.localBulb.config, 'enablePdfReport', 0);
        this.$set(this.localBulb.config, 'enableHtmlReport', 0);
        this.$set(this.localBulb.config, 'enableDocxReport', 0);
      }
      if (this.service === 'dashboard') this.$store.dispatch('instructors/getTags', { isSearch: true });
      const playback = this.modes.includes('lmb') ? 0 : 1;
      if (![1, 0].includes(_.get(this, 'localBulb.config.fast_playback'))) this.$set(this.localBulb.config, 'fast_playback', playback);
      this.$set(this, 'originalBulb', _.cloneDeep(this.localBulb));
      if (save) this.formatDataForSave();
      if (_.isFunction(callback)) callback();
      if (this.modes.includes('lmb')) {
        this.localBulb.config.fast_playback = 0;
      }
      if (
        this.localBulb?.config?.retry_yes
        && !_.isFinite(this.localBulb?.config?.attempts)
      ) {
        /*
          If a bulb allows retakes but doesn't have a `config.attempts` parameter, then the bulb is from before we allowed users to specify how many times a learner could retake a bulb. Because of this, we default the bulb to have an `attempts` value of 0, meaning unlimited attempts.
        */
        this.localBulb.config.attempts = 0;
      }
      // hacky... but will suffice for now. This is needed to de-couple the playlist score threshold from the normal
      // bulb-settings score threshold flow.
      if (
        this.service === 'playlist'
        && this.moduleBulb?.config?.scoreThreshold
      ) {
        this.$set(this.localBulb.config, 'scoreThreshold', this.moduleBulb?.config?.scoreThreshold);
        this.$set(this.originalBulb.config, 'scoreThreshold', this.moduleBulb?.config?.scoreThreshold);
      }
    },
    initBulbCheckout() {
      if (
        _.isEmpty(this.localBulb?.config?.checkout)
        || !this.localBulb?.config?.checkout?.enabled
      ) {
        this.checkoutDetails = {
          studentEmail: '',
          studentId: null,
          checkoutDate: '',
          checkoutTime: '',
          action: null,
        };
        this.originalCheckoutDetails = _.cloneDeep(this.checkoutDetails);
        return;
      }
      const cloned = _.cloneDeep(_.omit(this.localBulb?.config?.checkout, ['time']));
      for (const key in cloned) {
        this.$set(this.checkoutDetails, key, cloned[key]);
      }
      const checkoutDateTime = DateTime.fromMillis(this.localBulb?.config?.checkout?.time).toUTC().setZone(this.timezone).toISO();
      this.$set(this.checkoutDetails, 'checkoutDate', checkoutDateTime.split('T')[0]);
      this.$set(this.checkoutDetails, 'checkoutTime', checkoutDateTime.split('T')[1].split('.')[0]);
      this.$set(this.checkoutDetails, 'action', null);
      this.originalCheckoutDetails = _.cloneDeep(this.checkoutDetails);
    },
    async getBulbCheckoutStudentEmail(studentId) {
      try {
        this.isLoadingStudentEmail = true;
        const { data: { email } } = await this.api.getStudentDetails(studentId);
        this.$set(this.checkoutDetails, 'studentEmail', email);
        this.$set(this.originalCheckoutDetails, 'studentEmail', email);
      } catch(e) {
        this.$emit('snackbar', {
          text: `Failed to get the email address of the student who has checked out the ${this.termForBulb}.`,
          color: 'error',
          timeout: 3000,
        });
      } finally {
        this.isLoadingStudentEmail = false;
      }
    },
    applyTagSearchString: _.debounce(function debouncedApplyTagSearchString(searchString) {
      if (_.isNil(searchString)) searchString = '';
      // reset tag matches search if input is less than 4 characters
      if (searchString.length < 3) this.tagMatchesSearched = false;
      // Trigger tag matches search when input = 4 characters and we haven't searched yet
      if (searchString.length >= 3 && !this.tagMatchesSearched) {
        const data = { searchString, isSearch: true };
        this.tagMatchesSearched = true;
        if ([
          'dashboard',
          'batch',
          'batchApplyToAll',
        ].includes(this.service)) {
          this.$store.dispatch('instructors/getTags', data);
        }
        if (this.service === 'designer') this.getTags(searchString);
      }
    }, 275),
    async getBulbProquestStatus() {
      if (!this.bulb.id) return;
      const { data: { isProquestBulb } } = await this.api.getBulbProquestStatus(this.bulb.id);
      this.isProquestBulb = isProquestBulb;
    },
    async getBulbInteractions() {
      if (!this.bulb.id) return;
      const { data: { data: bulbInteractions } } = await this.api.getBulbInteractions(this.bulb.id);
      this.bulbInteractions = bulbInteractions;
    },
    resetScoreThreshold() {
      this.$set(this.localBulb.config, 'scoreThreshold', null);
      this.scoreThresholdPlaceholder = '100';
      this.isScoreThresholdEnabled = false;
    },
    setHasRevertedScoreThresholdProperty() {
      this.hasRevertedScoreThreshold = true;
    },
  },
  watch: {
    localBulb: {
      handler(n) {
        this.haveChangesBeenMade = (
          !_.isEqual(n, this.originalBulb)
          || !_.isEqual(this.originalCheckoutDetails, this.checkoutDetails)
        );
      },
      deep: true,
    },
    checkoutDetails: {
      handler() {
        this.haveChangesBeenMade = (
          !_.isEqual(this.localBulb, this.originalBulb)
          || !_.isEqual(this.checkoutDetails, this.originalCheckoutDetails)
        );
      },
      deep: true,
    },
    isFormValid(n) {
      if (n) {
        this.$set(this, 'formErrors', []);
        return;
      }
      if (!_.get(this, 'localBulb.name', '').length) this.formErrors.push('name');
      if (
        this.isScoreThresholdEnabled
        && !_.isFinite(this?.localBulb?.config?.scoreThreshold)
      ) this.formErrors.push('scoreThreshold');
      for (const key of ['keywords', 'age', 'subject']) {
        if (!_.get(this, '$refs["keywords"].valid')) this.formErrors.push(key);
      }
    },
    tagSearchString(n) {
      this.applyTagSearchString(n);
    },
    isScoreThresholdEnabled(n) {
      if (n) {
        this.$set(this.localBulb.config, 'scoreThreshold', Number(this.scoreThresholdPlaceholder));
      } else {
        this.$set(this.localBulb.config, 'scoreThreshold', null);
        this.scoreThresholdPlaceholder = this?.$refs?.scoreThresholdInput?.value?.toString();
      }
    },
    openedExpansionPanel(newValue) {
      if (
        newValue === 3
        && this.checkoutDetails.studentId !== this.previousBulbCheckoutStudentId
      ) {
        this.previousBulbCheckoutStudentId = this.checkoutDetails.studentId;
        if (this.checkoutDetails.studentId) this.getBulbCheckoutStudentEmail(this.checkoutDetails.studentId);
      }
      if (
        newValue === 1 // playback expansion settings panel
        && this.service === 'playlist'
      ) {
        this.playlistBulbSettingsSlideoutOpened = !this.playlistBulbSettingsSlideoutOpened;
        this.isScoreThresholdToggleEnabled; // eslint-disable-line
      }
    },
  },
  async created() {
    this.api.init();
    await this.getBulbProquestStatus();
    this.initBulb();
    this.initBulbCheckout();
  },
  beforeDestroy() {
    if (
      this.service === 'batch'
      && this.haveChangesBeenMade
    ) {
      this.formatDataForSave();
    }
    if (
      this.haveChangesBeenMade
      && this.hasRevertedScoreThreshold
    ) {
      this.formatDataForSave();
    }
  },
};
</script>

<style lang="scss">
.playposit-bulb-settings {
  .width-100 {
    width: 100% !important;
  }

  .light-gray-border {
    border-color: rgba(0,0,0,0.1);
  }

  .v-expansion-panel {
    .v-expansion-panel__container {
      border-top: 0;
      background-color: transparent !important;
    }

    .v-expansion-panel__header {
      padding: 12px 4px;

      &:hover {
        background: rgba(0,0,0,.04);
      }
    }
  }

  .small-label label {
    font-size: 14px !important;
  }

  .save-container {
    width: 100%;
  }

  .v-input--selection-controls.v-input .v-label {
    margin-bottom: 0;
  }

  .v-input--radio-group--column .v-radio:not(:last-child):not(:only-child) {
    margin-bottom: 0px;
  }

  .percentage-number-input {
    width: 64px;
    display: inline-block;
  }

  .indented-text {
    margin-left: 32px;
  }

  .v-select .v-select__selection {
    color: rgba(0, 0, 0, 0.87);
  }
}

.bulb-settings-select-menu {
  div[role="listitem"] > a.v-list__tile {
    min-height: 48px;
    height: auto;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    padding: 4px 16px;
  }
  .v-list__tile:not(.v-list__tile--disabled) .v-list__tile__title {
    color: rgba(0, 0, 0, 0.87);
  }
}

.privacyLabelStyle {
  background: inherit !important;
}
</style>